/**
 * The Used-by panel — ADR-127 D4.
 *
 * "Every schema version and every data set gets a Used by panel: the list of skills,
 * workflows, and crons that pin it, each with file, line, and click-through." Read-only, and
 * reusing the impact-review presentation `DeletePanel.tsx` establishes, "so retirement impact
 * and usage look and behave the same way".
 *
 * WHAT THIS PANEL MUST NEVER DO
 * -----------------------------
 * Render "nothing uses this" when it could not find out. D4's own argument is that "a
 * heuristic usage list is worse than none, because it invites an operator to trust a
 * retirement decision it cannot actually support" — and the same logic applies one level up:
 * an EMPTY list is the answer that unblocks a retirement, so showing it for a failed read is
 * the same mistake with a different cause. The endpoint returns 503 rather than an empty list
 * precisely so this component can tell the difference, and it does.
 *
 * Two smaller honesty obligations follow the same rule:
 *   - a CRON is an indirect edge through the workflow it drives, and is labelled as such,
 *     because "this cron will break" and "this workflow will break" read differently;
 *   - when `role_bindings_readable` is false a role-bound consumer may be MISSING, so the
 *     panel says the list may be incomplete rather than presenting it as exhaustive. That
 *     flag now means "the policy file is there and could not be parsed" and nothing else:
 *     ADR-147 D3 moved the read onto the delete plan's own snapshot, which can tell an
 *     ABSENT file (no bindings exist — the list IS exhaustive, and four of five fleets are
 *     in this state) from an unparseable one. The caveat used to show permanently for those
 *     fleets, which is the way a warning teaches an operator to ignore it.
 */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { DataConsumer, DataUsedBy } from '../api/client'

export interface DataUsedByPanelProps {
  /** `<name>@v<N>`. */
  set: string
  /** Optional click-through, given the consumer's repo-relative path and line. */
  onOpen?: (path: string, line: number) => void
}

const KIND_LABEL: Record<DataConsumer['kind'], string> = {
  skill: 'skill',
  workflow: 'workflow',
  cron: 'cron',
}

export function DataUsedByPanel({ set, onOpen }: DataUsedByPanelProps) {
  const [data, setData] = useState<DataUsedBy | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setData(null)
    setError(null)
    api.getDataReferenceUsedBy(set)
      .then((d) => { if (live) setData(d) })
      .catch((e: Error) => { if (live) setError(e.message || 'could not read usage') })
    return () => { live = false }
  }, [set])

  if (error) {
    // NOT an empty list. "Nothing uses this" is what unblocks a retirement, and saying it
    // because the read failed is how a dangling reference ships.
    return (
      <div data-testid="used-by-unavailable" role="alert">
        <h3>Used by</h3>
        <p>Usage could not be determined: {error}</p>
        <p>
          This is <strong>not</strong> the same as “nothing uses this” — no usage list is being
          shown, so do not treat this set as safe to retire.
        </p>
      </div>
    )
  }

  if (!data) return <div data-testid="used-by-loading">Loading usage…</div>

  if (data.count === 0) {
    return (
      <div data-testid="used-by-empty">
        <h3>Used by</h3>
        <p>Nothing pins {set}.</p>
        {!data.role_bindings_readable && (
          <p data-testid="used-by-incomplete">
            Role bindings could not be read, so a consumer that pins this set by ROLE would not
            appear here. Treat this list as incomplete.
          </p>
        )}
      </div>
    )
  }

  return (
    <div data-testid="used-by">
      <h3>
        Used by {data.count} {data.count === 1 ? 'consumer' : 'consumers'}
      </h3>
      {!data.role_bindings_readable && (
        <p data-testid="used-by-incomplete">
          Role bindings could not be read, so a consumer that pins this set by ROLE may be
          missing. Treat this list as incomplete.
        </p>
      )}
      <ul>
        {data.consumers.map((consumer) => (
          <li key={`${consumer.kind}:${consumer.name}:${consumer.path}`}
              data-testid={`used-by-row-${consumer.kind}-${consumer.name}`}>
            <span data-testid={`used-by-kind-${consumer.name}`}>
              {KIND_LABEL[consumer.kind]}
            </span>
            {' '}
            <strong>{consumer.name}</strong>
            {' '}
            {onOpen ? (
              <button
                data-testid={`used-by-open-${consumer.name}`}
                onClick={() => onOpen(consumer.path, consumer.line)}
              >
                {consumer.path}:{consumer.line}
              </button>
            ) : (
              <span>{consumer.path}:{consumer.line}</span>
            )}
            {consumer.indirect && (
              // Marked, not hidden: a cron declares no pin, it drives a workflow that does.
              <em data-testid={`used-by-indirect-${consumer.name}`}>
                {' '}via {consumer.via} — this cron does not pin the set itself
              </em>
            )}
          </li>
        ))}
      </ul>
      <p data-testid="used-by-exactness">
        Computed from declared <code>data:</code> pins, so this list is exact — a body that
        mentions the set without declaring a pin is a validation error, not a dependency.
      </p>
    </div>
  )
}
